// Der Spielzustand als reine Daten, getrennt vom Rendering (Architektur-Regel 1). Die Szene im
// Client ist nur eine Ansicht davon; die Simulation (step.ts) verändert ihn Tick für Tick.

import { createAnimal, type AnimalState } from '../animals/index.js';
import { createInventory, type Inventory } from '../inventory/index.js';
import type { Village } from '../world/types.js';

/** Simulationsschritte je Sekunde. */
export const TICK_RATE = 30;
/** Dauer eines Simulationsschritts in Sekunden. */
export const TICK_SECONDS = 1 / TICK_RATE;

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/**
 * Der Avatar. `pos` ist der Fußpunkt in der Mitte der Grundfläche, `vel` die Geschwindigkeit in
 * Blöcken je Sekunde, `yaw`/`pitch` die Blickrichtung im Bogenmaß (siehe docs/architektur.md,
 * Koordinaten und Einheiten).
 */
export interface PlayerState {
  pos: Vec3;
  vel: Vec3;
  yaw: number;
  pitch: number;
  /** Wahr, wenn im letzten Schritt die Bewegung nach unten von festem Boden gestoppt wurde. */
  onGround: boolean;
  flying: boolean;
}

/** Maße und Bewegungswerte des Avatars in Blöcken, Blöcken je Sekunde bzw. je Quadratsekunde. */
export const PLAYER = {
  width: 0.6,
  height: 1.8,
  eyeHeight: 1.62,
  walkSpeed: 4.3,
  flySpeed: 4.3,
  jumpSpeed: 8.4,
  gravity: 28,
  reach: 3,
} as const;

// AnimalState und createAnimal kommen aus shared/src/animals (ADR 0007). behavior.ts importiert
// umgekehrt PLAYER und die Takt-Konstanten von hier; der Kreis ist harmlos, weil beide Seiten die
// fremden Werte nur in Funktionen benutzen, nie auf Modulebene.

export interface GameState {
  tick: number;
  player: PlayerState;
  animals: AnimalState[];
  inventory: Inventory;
}

/**
 * Ein frischer Spielzustand zum Dorf: Avatar auf dem Startpunkt mit dessen Blickrichtung, je
 * Eintrag in `village.animals` ein Tier mit vollem Produkt, leeres Inventar. Das ist der einzige
 * Weg, auf dem Tiere in den Zustand kommen; Client und Server spawnen nichts selbst.
 */
export function createGameState(village: Village): GameState {
  const { x, y, z, yaw } = village.spawn;
  const tick = 0;
  return {
    tick,
    player: {
      pos: { x, y, z },
      vel: { x: 0, y: 0, z: 0 },
      yaw,
      pitch: 0,
      onGround: false,
      flying: false,
    },
    animals: village.animals.map((spawn) => createAnimal(spawn, tick)),
    inventory: createInventory(),
  };
}
